import type { Client, RequestContext } from "../client";
import type { PaginatedCollection, ProjectResource, DeploymentTaskDetails, RunbookRunTaskDetails } from "../resources";

export type GetEphemeralEnvironmentsForProjectBffResponse = {
    Environments: PaginatedCollection<ProjectEphemeralEnvironmentDetail>;
    StatusCounts: ProjectEphemeralEnvironmentStatusCounts;
    EphemeralEnvironmentCreationFailedEvents: EphemeralEnvironmentCreationFailedEvents;
};

export type ProjectEphemeralEnvironmentStatusCounts = {
    NotProvisioned: number;
    Provisioning: number;
    Provisioned: number;
    ProvisioningFailed: number;
    Deprovisioning: number;
    Deprovisioned: number;
    DeprovisioningFailed: number;
};

export type EphemeralEnvironmentCreationFailedEvents = {
    FailureSearchStartDate: string;
    LastFailureEvent?: string;
};

export type EphemeralEnvironmentProjectStatus = "NotProvisioned" | "Provisioning" | "ProvisioningFailed" | "Provisioned" | "Deprovisioning" | "DeprovisioningFailed" | "Deprovisioned";

export type RunbookRunCreationFailureDetail = {
    Occurred: string;
    Message: string;
};

export type ProjectDetails = {
    Id: string;
    Name: string;
    Logo: string;
};

export type ProjectEphemeralEnvironmentDetail = {
    Id: string;
    Status: EphemeralEnvironmentProjectStatus;
    Name: string;
    DeploymentTaskDetails?: DeploymentTaskDetails;
    RunbookRunTaskDetails?: RunbookRunTaskDetails;
    ProvisioningFailureDetails?: RunbookRunCreationFailureDetail;
    DeprovisioningFailureDetails?: RunbookRunCreationFailureDetail;
    ProjectDetails?: ProjectDetails[];
};

export interface GetEphemeralEnvironmentsOnboardingDataBffResponse {
    Project: EphemeralEnvironmentsOnboardingProjectDetails;
    ParentEnvironments: EphemeralEnvironmentsOnboardingEnvironment[];
    Runbooks: EphemeralEnvironmentsOnboardingRunbook[];
    Permissions: EphemeralEnvironmentsOnboardingPermissions;
}

export type ProjectIdAndName = {
    ProjectId: string;
    ProjectName: string;
};

export type GetProjectsForEphemeralEnvironmentsResponse = {
    Projects: ProjectIdAndName[];
};

export type EphemeralEnvironmentsOnboardingEnvironment = {
    Id: string;
    Name: string;
};

export type EphemeralEnvironmentsOnboardingRunbook = {
    Id: string;
    Name: string;
};

export type EphemeralEnvironmentsOnboardingProjectDetails = EphemeralEnvironmentsOnboardingProjectDetailsDatabase | EphemeralEnvironmentsOnboardingProjectDetailsGit;

export type EphemeralEnvironmentsOnboardingProjectDetailsDatabase = {
    GitRunbooks: false;
};

export type EphemeralEnvironmentsOnboardingProjectDetailsGit = {
    GitRunbooks: true;
    DefaultBranch: string;
    DefaultBranchIsProtected: boolean;
};

export type EphemeralEnvironmentsOnboardingPermissions = {
    ViewEnvironments: boolean;
    CreateEnvironments: boolean;
    CreateChannels: boolean;
    ConfigureRunbooks: boolean;
    ViewRunbooks: boolean;
    CreateRunbooks: boolean;
};

export interface ConfigureEphemeralEnvironmentsBffCommand {
    SpaceId: string;
    ProjectId: string;
    EphemeralEnvironmentNameTemplate: string;
    ParentEnvironment: ParentEnvironmentSelection;
    ProvisioningRunbook?: RunbookSelection;
    DeprovisioningRunbook?: RunbookSelection;
    RunbooksBranch?: string;
    AutomaticEphemeralEnvironmentDeployments?: boolean;
}

export interface ConfigureEphemeralEnvironmentsBffResponse {
    ChannelName: string;
}

export interface ParentEnvironmentSelection {
    NewName?: string;
    ExistingId?: string;
}

export interface RunbookSelection {
    NewName?: string;
    ExistingId?: string;
}

export interface CreateEphemeralEnvironmentCommand {
    SpaceId: string;
    ProjectId: string;
    EnvironmentName: string;
}

export interface CreateEphemeralEnvironmentResponse {
    Id: string;
}

export class EphemeralEnvironmentRepository {
    protected client: Client;

    constructor(
        client: Client,
        private readonly requestContext: RequestContext
    ) {
        this.client = client;
    }

    async create(command: CreateEphemeralEnvironmentCommand) {
        return await this.client.post<CreateEphemeralEnvironmentResponse>(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/ephemeral", command, { spaceId: command.SpaceId, projectId: command.ProjectId });
    }

    async getForProject(project: ProjectResource, skip: number, take: number, name?: string, status?: EphemeralEnvironmentProjectStatus[]) {
        return await this.client.get<GetEphemeralEnvironmentsForProjectBffResponse>(this.requestContext, "~/bff/spaces/{spaceId}/projects/{projectId}/ephemeral-environments{?skip,take,name,status*}", {
            spaceId: project.SpaceId,
            projectId: project.Id,
            skip: skip,
            take: take,
            name: name,
            status: status,
        });
    }

    async getProjectsForEphemeralEnvironments(spaceId: string, deploymentEnvironmentId: string) {
        return await this.client.get<GetProjectsForEphemeralEnvironmentsResponse>(this.requestContext, "~/bff/spaces/{spaceId}/ephemeral-environments/{deploymentEnvironmentId}/projects", { spaceId, deploymentEnvironmentId });
    }

    async getOnboardingData(project: ProjectResource) {
        return await this.client.get<GetEphemeralEnvironmentsOnboardingDataBffResponse>(this.requestContext, "~/bff/spaces/{spaceId}/projects/{projectId}/ephemeral-environments/configure", {
            spaceId: project.SpaceId,
            projectId: project.Id,
        });
    }

    async configureEphemeralEnvironments(spaceId: string, projectId: string, command: ConfigureEphemeralEnvironmentsBffCommand) {
        return await this.client.post<ConfigureEphemeralEnvironmentsBffResponse>(this.requestContext, "~/bff/spaces/{spaceId}/projects/{projectId}/ephemeral-environments/configure", command, { spaceId, projectId });
    }

    async deprovision(spaceId: string, id: string) {
        return await this.client.post(this.requestContext, "~/api/spaces/{spaceId}/environments/ephemeral/{id}/deprovision", undefined, { spaceId, id });
    }

    async deprovisionForProject(spaceId: string, projectId: string, environmentId: string) {
        return await this.client.post(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/ephemeral/{environmentId}/deprovision", undefined, { spaceId, projectId, environmentId });
    }

    async retryProvisioning(spaceId: string, projectId: string, environmentId: string) {
        return await this.client.post(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/ephemeral/{environmentId}/provisioning/retry", undefined, { spaceId, projectId, environmentId });
    }

    async retryDeprovisioning(spaceId: string, projectId: string, environmentId: string) {
        return await this.client.post(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/ephemeral/{environmentId}/deprovisioning/retry", undefined, { spaceId, projectId, environmentId });
    }

    async markProvisioningSuccessful(spaceId: string, projectId: string, environmentId: string) {
        return await this.client.post(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/ephemeral/{environmentId}/provisioning/mark-successful", undefined, { spaceId, projectId, environmentId });
    }

    async markDeprovisioningSuccessful(spaceId: string, projectId: string, environmentId: string) {
        return await this.client.post(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/ephemeral/{environmentId}/deprovisioning/mark-successful", undefined, { spaceId, projectId, environmentId });
    }
}
